import { useState } from "react";
import styled from "styled-components";
import { useSnackbar } from "notistack";

import {
  Avatar,
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";

import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import NorthWestIcon from "@mui/icons-material/NorthWest";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import {
  addTokenToUser,
  getTokenBalanceByUser,
} from "src/api/tokenTransaction";

import { IconToken } from "src/icons";
import font from "src/styles/font";
import device from "src/styles/device";
import { Black, DarkGrey, Grey, White, White01 } from "src/styles/color";
import { checkNotEmpty, convertIntToCurrency } from "src/common";
import { hexToRgba } from "src/common/color";
import { UseUpload } from "src/context/UploadProductContext";

import Main from "src/components/ui/Container";
import Loading from "src/components/ui/Loading";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";

const Container = styled.div`
  width: 100%;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px;
`;

const SpanHeader = styled.div`
  color: ${Black};
  font-size: ${font.size.xl};
  font-weight: 600;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.lg};
  }
`;

const SpanSubHeader = styled.div`
  color: ${DarkGrey};
  font-size: ${font.size.md};
  font-weight: 400;
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const Wrapper = styled(Box)`
  width: 100%;
  border-radius: 16px;
  border: 1px solid ${White01};
  background-color: ${White};
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px -1px rgba(0, 0, 0, 0.1);
`;

const SpanTitle = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.lg};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.md};
  }
`;

const SpanItemName = styled.div`
  color: ${({ color }) => (color ? color : Black)};
  font-size: ${font.size.md};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 500)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  @media ${device.tabletL} {
    font-size: ${font.size.sm};
  }
`;

const SpanSubItem = styled.div`
  color: ${({ color }) => (color ? color : Grey)};
  font-size: ${font.size.sm};
  font-weight: ${({ fontWeight }) => (fontWeight ? fontWeight : 400)};
  margin-top: ${({ mt }) => (mt ? `${mt}px` : 0)};
  margin-bottom: ${({ mb }) => (mb ? `${mb}px` : 0)};

  span {
    font-size: ${font.size.sm};
    font-weight: 500;
  }

  @media ${device.tabletL} {
    font-size: ${font.size.xs};
  }
`;

const SpanTokenAmount = styled.div`
  color: ${White};
  font-size: 2rem;
  font-weight: 700;

  @media ${device.tabletL} {
    font-size: 1.75rem;
  }
`;

const WrapperSelectSeller = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const WrapperFormAction = styled(Wrapper)`
  padding: 24px;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WrapperCurrentBalance = styled(Wrapper)`
  margin-top: 24px;
  padding: 16px;
  background-color: #155dfc;
  color: ${White};
`;

const WrapperTypeTopup = styled(Wrapper)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  cursor: pointer;
  text-align: center;

  &:hover {
    background-color: ${hexToRgba(White01, 0.5)};
  }

  &.active-1 {
    color: #f54900;
    background-color: #fff7ed;
    border: 1px solid #f54900;

    .MuiSvgIcon-root,
    .MuiTypography-root {
      color: #f54900;
    }
  }
  &.active-2 {
    color: #9810fa;
    background-color: #f3e8ff;
    border: 1px solid #9810fa;

    .MuiSvgIcon-root,
    .MuiTypography-root {
      color: #9810fa;
    }
  }
  &.active-3 {
    color: #155dfc;
    background-color: #eff6ff;
    border: 1px solid #155dfc;

    .MuiSvgIcon-root,
    .MuiTypography-root {
      color: #155dfc;
    }
  }
  &.active-4 {
    color: #00a63e;
    background-color: #f0fdf4;
    border: 1px solid #00a63e;

    .MuiSvgIcon-root,
    .MuiTypography-root {
      color: #00a63e;
    }
  }
`;

const SpecialTokenTopup = ({ open }) => {
  const { masterdata } = UseUpload();
  const { seller_data } = masterdata;
  const { enqueueSnackbar } = useSnackbar();

  const [filterSeller, setFilterSeller] = useState("");
  const defaultData = {
    seller: null,
    type: "",
    // params
    amount: 0,
    expiry_days: 0,
    note: "",
  };
  const [formData, setFormData] = useState(defaultData);
  const [isLoading, setIsLoading] = useState(false);

  const isFormValid = ["type", "amount", "expiry_days", "note"].every(
    (required) => {
      let objVal = formData[required];
      return checkNotEmpty(objVal);
    },
  );

  const seller_option = seller_data
    .filter((sel) => checkNotEmpty(sel.seller.company_name))
    .map((sel) => {
      return {
        ...sel,
        _id: sel.user._id,
        label: sel.seller.company_name,
        balance: {},
      };
    });

  const stringAvatar = (name) => {
    return {
      children:
        `${name.split(" ")[0][0]}${name.split(" ")[1] ? name.split(" ")[1][0] : name.split(" ")[0][1]}`.toLocaleUpperCase(),
    };
  };

  const handleSelectSeller = async (seller) => {
    try {
      setIsLoading(true);
      const balanceData = await getTokenBalanceByUser(seller._id);

      setFilterSeller(seller.label);
      setFormData((prev) => ({
        ...prev,
        seller: { ...seller, balance: balanceData },
      }));
    } catch (error) {
      console.error("Error fetch seller token wallet: ", error);
    } finally {
      setIsLoading(false);
    }
  };

  const submitForm = async () => {
    try {
      setIsLoading(true);

      const response = await addTokenToUser(formData.seller._id, {
        amount: Number(formData.amount),
        expiry_days: Number(formData.expiry_days),
        note: `${
          formData.type === "compensation"
            ? "ชดเชยปัญหา: "
            : formData.type === "voucher"
              ? "Voucher: "
              : formData.type === "promotion"
                ? "โปรโมชั่น: "
                : formData.type === "reward"
                  ? "รางวัลพิเศษ: "
                  : ""
        }${formData.note}`,
      });

      if (response.success) {
        enqueueSnackbar(
          `เพิ่ม ${response.data.adjusted_amount} Tokens สำเร็จ`,
          {
            variant: "success",
          },
        );

        // Refresh token balance
        const balanceData = await getTokenBalanceByUser(formData.seller._id);
        setFormData((prev) => ({
          ...defaultData,
          seller: { ...prev.seller, balance: balanceData },
        }));
      }
    } catch (error) {
      console.error("Error submit token topup: ", error);
      enqueueSnackbar(`เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้งในภายหลัง`, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Main open={open}>
        <Container>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid item xs={12}>
              <SpanHeader mb={2}>มอบ Token ให้ผู้ขาย (Manual Grant)</SpanHeader>
              <SpanSubHeader>
                ใช้สำหรับกรณีชดเชยยอด, ให้เป็นรางวัลพิเศษ หรือ Voucher
                ส่วนลดต่างๆ
              </SpanSubHeader>
            </Grid>

            {/* Select Seller */}
            <Grid item xs={12} md={4}>
              <WrapperSelectSeller>
                <Box
                  sx={{
                    width: "100%",
                    p: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <PersonIcon />
                  <SpanTitle>เลือกผู้ขาย</SpanTitle>
                </Box>

                <Divider sx={{ width: "100%", borderColor: White01 }} />

                <Box sx={{ width: "100%", p: 2 }}>
                  <TextField
                    fullWidth
                    placeholder="ค้นหาชื่อผู้ขาย"
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: Grey }} />,
                      endAdornment: filterSeller && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFilterSeller("");
                            // setSelectedSeller(null);
                            setFormData(defaultData);
                          }}
                          sx={{
                            visibility: filterSeller ? "visible" : "hidden",
                          }}
                        >
                          <CloseIcon sx={{ color: Grey, fontSize: 20 }} />
                        </IconButton>
                      ),
                    }}
                    value={filterSeller}
                    onChange={(e) => setFilterSeller(e.target.value)}
                  />

                  <Stack
                    mt={2}
                    spacing={1}
                    sx={{ maxHeight: { xs: 300, md: 450 }, overflowY: "auto" }}
                  >
                    {seller_option.length > 0 ? (
                      seller_option
                        .filter((seller) =>
                          seller.label
                            .toLowerCase()
                            .includes(filterSeller.toLowerCase()),
                        )
                        .map((seller) => (
                          <Box
                            key={seller?._id}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              px: 2,
                              py: 1.5,
                              borderRadius: 3,
                              border: `1px solid transparent`,
                              cursor: "pointer",
                              ...(formData?.seller?._id === seller?._id && {
                                backgroundColor: "#EFF6FF",
                                border: `1px solid #BEDBFF`,
                              }),
                              "&:hover": {
                                backgroundColor:
                                  formData?.seller?._id === seller?._id
                                    ? "#EFF6FF"
                                    : White01,
                              },
                            }}
                            onClick={() => handleSelectSeller(seller)}
                          >
                            <Avatar
                              sx={{
                                fontSize: font.size.md,
                                bgcolor:
                                  formData?.seller?._id === seller?._id
                                    ? "#155DFC"
                                    : "default",
                              }}
                              {...(seller?.label
                                ? stringAvatar(seller?.label)
                                : { children: "SL" })}
                            />
                            <SpanItemName>{seller?.label}</SpanItemName>
                          </Box>
                        ))
                    ) : (
                      <Box sx={{ textAlign: "center", py: 2 }}>
                        <SpanSubItem>Loading...</SpanSubItem>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </WrapperSelectSeller>

              {formData?.seller && (
                <WrapperCurrentBalance>
                  <SpanSubItem color={White}>CURRENT BALANCE</SpanSubItem>

                  <Stack direction="row" alignItems="center" gap={1}>
                    <IconToken fill={White} width={32} height={32} />
                    <SpanTokenAmount>
                      {formData?.seller?.balance
                        ? convertIntToCurrency(
                            formData.seller?.balance?.current_balance,
                            false,
                          )
                        : 0}
                    </SpanTokenAmount>
                    <SpanSubItem color={White}>Tokens</SpanSubItem>
                  </Stack>
                </WrapperCurrentBalance>
              )}
            </Grid>

            <Grid item xs={12} md={8}>
              <WrapperFormAction>
                {checkNotEmpty(formData?.seller) ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <SpanItemName mb={8}>ประเภทการมอบ Token</SpanItemName>

                      <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                          <WrapperTypeTopup
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "compensation",
                              }))
                            }
                            className={
                              formData.type === "compensation" ? "active-1" : ""
                            }
                          >
                            <WarningAmberIcon
                              sx={{ color: DarkGrey, fontSize: 28 }}
                            />

                            <Typography variant="subtitle2">
                              ชดเชยปัญหา
                            </Typography>
                          </WrapperTypeTopup>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <WrapperTypeTopup
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "voucher",
                              }))
                            }
                            className={
                              formData.type === "voucher" ? "active-2" : ""
                            }
                          >
                            <CardGiftcardIcon
                              sx={{ color: DarkGrey, fontSize: 28 }}
                            />

                            <Typography variant="subtitle2">Voucher</Typography>
                          </WrapperTypeTopup>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <WrapperTypeTopup
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "promotion",
                              }))
                            }
                            className={
                              formData.type === "promotion" ? "active-3" : ""
                            }
                          >
                            <AutoAwesomeOutlinedIcon
                              sx={{ color: DarkGrey, fontSize: 28 }}
                            />

                            <Typography variant="subtitle2">
                              โปรโมชั่น
                            </Typography>
                          </WrapperTypeTopup>
                        </Grid>

                        <Grid item xs={6} md={3}>
                          <WrapperTypeTopup
                            onClick={() =>
                              setFormData((prev) => ({
                                ...prev,
                                type: "reward",
                              }))
                            }
                            className={
                              formData.type === "reward" ? "active-4" : ""
                            }
                          >
                            <VerifiedUserOutlinedIcon
                              sx={{ color: DarkGrey, fontSize: 28 }}
                            />

                            <Typography variant="subtitle2">
                              รางวัลพิเศษ
                            </Typography>
                          </WrapperTypeTopup>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <SpanItemName mb={8}>
                        จำนวน Token ที่ต้องการมอบ
                      </SpanItemName>

                      <TextField
                        fullWidth
                        placeholder="กรอกจำนวน Token ที่ต้องการมอบ"
                        InputProps={{
                          startAdornment: (
                            <IconToken width={24} height={24} fill={Grey} />
                          ),
                        }}
                        value={formData.amount || ""}
                        onChange={(e) => {
                          if (!/^\d*$/.test(e.target.value)) return; // allow only numbers
                          setFormData((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }));
                        }}
                      />

                      <Typography mt={1} color={Grey} fontSize={font.size.xs}>
                        * จำนวน Token จะถูกเพิ่มเข้าไปในยอดคงเหลือปัจจุบันทันที
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <SpanItemName mb={8}>
                        อายุการใช้งานของ Token (วัน)
                      </SpanItemName>

                      <TextField
                        fullWidth
                        placeholder="กรอกจำนวนวันก่อนที่ Token จะหมดอายุ (เช่น 30)"
                        InputProps={{
                          startAdornment: (
                            <ScheduleIcon sx={{ color: Grey, fontSize: 20 }} />
                          ),
                        }}
                        value={formData.expiry_days || ""}
                        onChange={(e) => {
                          if (!/^\d*$/.test(e.target.value)) return; // allow only numbers
                          setFormData((prev) => ({
                            ...prev,
                            expiry_days: e.target.value,
                          }));
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <SpanItemName mb={8}>
                        หมายเหตุ / เหตุผลเพิ่มเติม
                      </SpanItemName>

                      <TextField
                        fullWidth
                        placeholder="ระบุรายละเอียด เช่น เลขเคสปัญหา หรือแคมเปญ..."
                        InputProps={{
                          startAdornment: (
                            <ChatBubbleOutlineIcon
                              sx={{ color: Grey, fontSize: 20 }}
                            />
                          ),
                        }}
                        value={formData.note}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            note: e.target.value,
                          }))
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        gap={1}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          border: "1px solid #FEF3C6",
                          backgroundColor: "#FFFBEB",
                        }}
                      >
                        <ErrorOutlineIcon sx={{ color: "#E17100" }} />

                        <Box>
                          <SpanItemName color="#7B3306" mb={4}>
                            ข้อควรระวัง
                          </SpanItemName>

                          <SpanSubItem color="#7B3306">
                            การเพิ่ม Token แบบกำหนดเองจะถูกบันทึกเป็นรายการ{" "}
                            <span>"System Adjusted"</span>{" "}
                            ซึ่งจะปรากฏในหน้าธุรกรรมของผู้ขายและรายงานตรวจสอบของผู้ดูแลระบบ
                            (Audit Log) ไม่สามารถย้อนกลับได้
                          </SpanSubItem>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        size="large"
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon />}
                        disabled={!isFormValid}
                        onClick={submitForm}
                      >
                        {`ยืนยันการเพิ่ม ${formData.amount || 0} Tokens`}
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 2,
                      height: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: White01,
                        p: 1,
                        borderRadius: "50%",
                        height: 60,
                        width: 60,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <NorthWestIcon sx={{ color: Grey, fontSize: 32 }} />
                    </Box>
                    <SpanTitle color={DarkGrey}>กรุณาเลือกผู้ขายก่อน</SpanTitle>
                    <SpanSubItem>
                      เลือกผู้ขายจากรายการด้านซ้ายเพื่อดำเนินการมอบ Token
                    </SpanSubItem>
                  </Box>
                )}
              </WrapperFormAction>
            </Grid>
          </Grid>
        </Container>
      </Main>

      {isLoading && <Loading />}
    </>
  );
};

export default SpecialTokenTopup;
